import React, { RefObject, useEffect, useMemo, useRef } from 'react';
import { IMappedField, MapModeEnum } from '../../../slice/types';
import { mapColors } from '../../../slice/map-constants';
import { Marker, Polygon } from 'react-leaflet';
import { createFieldMarker } from '../../../../../components/MarkerIcon/MarkerIcon';
import {
  LeafletEventHandlerFnMap,
  PathOptions,
  Polygon as PolygonType,
} from 'leaflet';
import { selectFieldsOpacity } from 'app/containers/Map/slice/selectors';
import { useSelector } from 'react-redux';

interface IProps {
  mappedField: IMappedField;
  isSelected: boolean;
  isActive?: boolean;
  eventHandlers?: LeafletEventHandlerFnMap;
  mode?: MapModeEnum;
}

export function Field({
  isSelected,
  isActive = false,
  mappedField,
  eventHandlers,
  mode,
}: IProps) {
  const polygonRef: RefObject<PolygonType<any>> | null = useRef(null);
  const fieldsOpacity = useSelector(selectFieldsOpacity);
  const polygonPathOptions: PathOptions = useMemo(() => {
    const cultureColor = mappedField.fieldParameters.cultureColor as 'YELLOW';
    const color = cultureColor || mapColors.colors.DEFAULT;
    return {
      fillColor: color,
      color: color,
      opacity: fieldsOpacity === 1 ? 0.9 : fieldsOpacity,
      fillOpacity: fieldsOpacity === 1 ? 0.9 : fieldsOpacity,
    };
  }, [mappedField, fieldsOpacity]);

  useEffect(() => {
    if (polygonRef.current) {
      polygonRef.current.setStyle(
        isSelected
          ? {
              color: mapColors.polygon.selected.color,
              fillColor:
                mappedField.fieldParameters.cultureColor ||
                mapColors.colors.DEFAULT,
            }
          : polygonPathOptions,
      );
    }
  }, [isSelected, polygonRef, polygonPathOptions, mappedField]);

  const markerIcon = useMemo(
    () =>
      createFieldMarker({
        selected: isSelected || isActive,

        percentage: mappedField.fieldProgressUpdate,
        name: mappedField.fieldParameters.name,
        culture: mappedField.fieldParameters.cultureName,
        colorClass: mappedField.fieldParameters.cultureColor,
        area: mappedField.fieldParameters.area,
        mode,
      }),
    [isSelected, mappedField, mode, isActive],
  );

  return (
    <>
      <Polygon
        pathOptions={polygonPathOptions}
        positions={mappedField.coordinates}
        eventHandlers={eventHandlers}
        ref={polygonRef}
      />
      <Marker
        position={mappedField.markerPosition}
        eventHandlers={eventHandlers}
        icon={markerIcon}
        opacity={fieldsOpacity}
      />
    </>
  );
}
